import {
  iconStartVideoStudent,
  iconStudentWatch,
  StudentCourseCarouselItem,
  StudentCourseCarouselSection,
  StudentDashboardMainAsideLayout,
  StudentDashboardShell,
  StudentGroupBadgeRow,
  StudentRankingPanel,
  StudentXpBanner,
  VideoProgressCard,
  useTheme,
} from '@e-learning/shared';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  mapFeedCourseToVideoCardProps,
  useStudentFeed,
  useStudentRanking,
  useStudentUserProfile,
} from './hooks';

type DashboardLocationState = { refreshStudentStats?: boolean };

export default function Dashboard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const {
    entries: rankingEntries,
    currentUser: rankingCurrentUser,
    loading: rankingLoading,
    error: rankingError,
    refetch: refetchRanking,
  } = useStudentRanking();

  const {
    continuing,
    watchAgain,
    available,
    loading: feedLoading,
    error: feedError,
    refetch: refetchFeed,
  } = useStudentFeed(search);

  const { userGroups, loading: profileLoading } = useStudentUserProfile();

  useEffect(() => {
    const st = location.state as DashboardLocationState | undefined;
    if (!st?.refreshStudentStats) return;
    refetchFeed();
    refetchRanking();
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {},
    });
  }, [
    location.state,
    location.pathname,
    location.search,
    navigate,
    refetchFeed,
    refetchRanking,
  ]);

  return (
    <StudentDashboardShell>
      <StudentDashboardMainAsideLayout
        main={
          <>
            <StudentXpBanner
              label={t('student_dashboard.xp_banner.total_xp')}
              value={rankingCurrentUser?.xp}
            />

            <StudentGroupBadgeRow
              groups={userGroups}
              loading={profileLoading}
              hint={t('student_dashboard.group_badge_hint')}
            />

            {feedError != null ? (
              <p
                role="alert"
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: theme.palette.error.main,
                }}
              >
                {t(feedError.key)}
              </p>
            ) : null}

            {!feedLoading && continuing.length > 0 ? (
              <StudentCourseCarouselSection
                title={t('student_dashboard.feed.section_in_progress')}
                titleKind="in_progress"
                countLabel={t('student_dashboard.feed.courses_count', {
                  count: continuing.length,
                })}
              >
                {continuing.map(course => {
                  const card = mapFeedCourseToVideoCardProps(
                    course,
                    'continuing'
                  );
                  return (
                    <StudentCourseCarouselItem key={course.courseId}>
                      <VideoProgressCard
                        coverImageUrl={card.coverImageUrl}
                        coverImageAlt={card.coverImageAlt}
                        xp={card.xp}
                        progressPercent={card.progressPercent}
                        title={card.title}
                        description={card.description}
                        status={card.status}
                        actionIconSrc={iconStartVideoStudent}
                        actionIconAlt=""
                        onActionClick={() => {
                          navigate(
                            `/student/meus-cursos?courseId=${encodeURIComponent(course.courseId)}`
                          );
                        }}
                      />
                    </StudentCourseCarouselItem>
                  );
                })}
              </StudentCourseCarouselSection>
            ) : null}

            {!feedLoading && available.length > 0 ? (
              <StudentCourseCarouselSection
                title={t('student_dashboard.feed.section_available')}
                titleKind="available_for_you"
                countLabel={t('student_dashboard.feed.courses_count', {
                  count: available.length,
                })}
              >
                {available.map(course => {
                  const card = mapFeedCourseToVideoCardProps(
                    course,
                    'available'
                  );
                  return (
                    <StudentCourseCarouselItem key={course.courseId}>
                      <VideoProgressCard
                        coverImageUrl={card.coverImageUrl}
                        coverImageAlt={card.coverImageAlt}
                        xp={card.xp}
                        progressPercent={card.progressPercent}
                        showProgress={card.showProgress}
                        leftTopBadgeLabel={card.leftTopBadgeLabel}
                        title={card.title}
                        description={card.description}
                        status={card.status}
                        onActionClick={() => {
                          navigate(
                            `/student/meus-cursos?courseId=${encodeURIComponent(course.courseId)}`
                          );
                        }}
                      />
                    </StudentCourseCarouselItem>
                  );
                })}
              </StudentCourseCarouselSection>
            ) : null}

            {!feedLoading && watchAgain.length > 0 ? (
              <StudentCourseCarouselSection
                title={t('student_dashboard.feed.section_watch_again')}
                titleKind="watch_again"
                countLabel={t('student_dashboard.feed.courses_count', {
                  count: watchAgain.length,
                })}
              >
                {watchAgain.map(course => {
                  const card = mapFeedCourseToVideoCardProps(
                    course,
                    'watchAgain'
                  );
                  return (
                    <StudentCourseCarouselItem key={course.courseId}>
                      <VideoProgressCard
                        coverImageUrl={card.coverImageUrl}
                        coverImageAlt={card.coverImageAlt}
                        xp={card.xp}
                        progressPercent={card.progressPercent}
                        showProgress={card.showProgress}
                        showXp={card.showXp}
                        title={card.title}
                        description={card.description}
                        status={card.status}
                        completedAt={card.completedAt}
                        actionButtonVariant={card.actionButtonVariant}
                        actionIconSrc={iconStudentWatch}
                        actionIconAlt=""
                        actionLabelOverride={t(
                          'video_progress_card.actions.review_course'
                        )}
                        onActionClick={() => {
                          navigate(
                            `/student/meus-cursos?courseId=${encodeURIComponent(course.courseId)}`
                          );
                        }}
                      />
                    </StudentCourseCarouselItem>
                  );
                })}
              </StudentCourseCarouselSection>
            ) : null}
          </>
        }
        aside={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rankingError != null ? (
              <p
                role="alert"
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: theme.palette.error.main,
                }}
              >
                {t(rankingError.key)}
              </p>
            ) : null}
            <StudentRankingPanel
              entries={rankingLoading ? [] : rankingEntries}
              currentUser={rankingLoading ? undefined : rankingCurrentUser}
            />
          </div>
        }
      />
    </StudentDashboardShell>
  );
}
